import * as React from "react";
import { MessageSquare, MoreHorizontal, RefreshCw, ShieldBan, Users } from "lucide-react";
import {
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import {
  useAdminStudyGroupBans,
  useAdminStudyGroupMessages,
  useAdminStudyGroups,
  useBanStudyGroupMember,
  useDeleteStudyGroupMessage,
  useRemoveStudyGroupMember,
  useSetStudyGroupArchived,
  useUnbanStudyGroupMember,
  type AdminStudyGroup,
} from "@/lib/learning/adminApi";

function formatWhen(iso: string): string {
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(iso),
  );
}

function BannedMembersPanel({ group }: { group: AdminStudyGroup }) {
  const bansQuery = useAdminStudyGroupBans(group.id);
  const unbanMutation = useUnbanStudyGroupMember(group.id);
  const bans = bansQuery.data?.items ?? [];

  const handleUnban = async (userId: string, name: string) => {
    try {
      await unbanMutation.mutateAsync(userId);
      toast.success(`${name} can rejoin the group.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not unban member.");
    }
  };

  return (
    <section
      aria-labelledby="banned-members-heading"
      className="border-border/60 bg-card mt-4 rounded-lg border p-4"
    >
      <div className="flex items-center gap-2">
        <ShieldBan aria-hidden="true" className="text-muted-foreground size-4" />
        <h3 id="banned-members-heading" className="text-sm font-medium">
          Banned members
        </h3>
        <span className="text-muted-foreground text-xs tabular-nums">
          {bansQuery.isLoading ? "…" : bans.length}
        </span>
      </div>
      {bansQuery.isError ? (
        <p className="text-destructive mt-2 text-sm">Could not load banned members.</p>
      ) : !bansQuery.isLoading && bans.length === 0 ? (
        <p className="text-muted-foreground mt-2 text-sm">No one is banned from this group.</p>
      ) : (
        <ul className="divide-border/60 mt-2 divide-y" data-testid="banned-members-list">
          {bans.map((ban) => (
            <li key={ban.user_id} className="flex items-center justify-between gap-3 py-2">
              <span className="truncate text-sm">{ban.display_name}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={unbanMutation.isPending && unbanMutation.variables === ban.user_id}
                onClick={() => void handleUnban(ban.user_id, ban.display_name)}
              >
                Unban
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function StudyGroupMessagesPanel({ group }: { group: AdminStudyGroup }) {
  const [page, setPage] = React.useState(0);
  const pageSize = 25;
  const messagesQuery = useAdminStudyGroupMessages(group.id, {
    limit: pageSize,
    offset: page * pageSize,
  });
  const deleteMutation = useDeleteStudyGroupMessage(group.id);
  const banMutation = useBanStudyGroupMember(group.id);
  const removeMutation = useRemoveStudyGroupMember(group.id);
  const items = messagesQuery.data?.items ?? [];
  const total = messagesQuery.data?.total ?? 0;

  const handleDelete = async (messageId: string) => {
    try {
      await deleteMutation.mutateAsync(messageId);
      toast.success("Message removed.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not remove message.");
    }
  };

  const handleBan = async (userId: string) => {
    try {
      await banMutation.mutateAsync(userId);
      toast.success("Member banned from group.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not ban member.");
    }
  };

  const handleRemove = async (userId: string) => {
    try {
      await removeMutation.mutateAsync(userId);
      toast.success("Member removed from group.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not remove member.");
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <DataTable>
        <DataTableToolbar>
          <DataTableToolbarSpacer />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => messagesQuery.refetch()}
            disabled={messagesQuery.isFetching}
          >
            <RefreshCw
              aria-hidden="true"
              className={cn("size-4", messagesQuery.isFetching && "animate-spin")}
            />
            Refresh
          </Button>
        </DataTableToolbar>
        <DataTableScroll>
          <Table>
            <caption className="sr-only">Study group messages for moderation</caption>
            <TableHeader>
              <TableRow>
                <TableHead>Author</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[1%] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            {messagesQuery.isLoading ? (
              <DataTableSkeleton rows={6} columns={5} />
            ) : (
              <TableBody>
                {items.map((msg) => (
                  <TableRow key={msg.id}>
                    <TableCell className="font-medium">{msg.author_name}</TableCell>
                    <TableCell className="max-w-[320px] truncate">{msg.body}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {formatWhen(msg.created_at)}
                    </TableCell>
                    <TableCell>
                      <StatusPill status={msg.is_deleted ? "archived" : "active"} />
                    </TableCell>
                    <TableCell className="text-right">
                      {!msg.is_deleted ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Message actions">
                              <MoreHorizontal aria-hidden="true" className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => void handleDelete(msg.id)}>
                              Delete message
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => void handleRemove(msg.author_user_id)}>
                              Remove member
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => void handleBan(msg.author_user_id)}
                            >
                              Ban member
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>
        {!messagesQuery.isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<MessageSquare aria-hidden="true" />}
            title="No messages"
            description="This group has no chat history yet."
          />
        ) : null}
        {!messagesQuery.isLoading && items.length > 0 ? (
          <DataTableFooter>
            <DataTablePagination
              page={page + 1}
              pageSize={pageSize}
              totalRows={total}
              onPageChange={(next) => setPage(Math.max(0, next - 1))}
              onPageSizeChange={() => {}}
              testIdPrefix="study-group-messages"
            />
          </DataTableFooter>
        ) : null}
      </DataTable>
    </div>
  );
}

export function LearningStudyGroupsTab() {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "learning-study-groups",
    defaultPageSize: 25,
  });
  const [activeGroup, setActiveGroup] = React.useState<AdminStudyGroup | null>(null);
  const query = useAdminStudyGroups({ limit: pageSize, offset: page * pageSize });
  const archiveMutation = useSetStudyGroupArchived();
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  const setArchived = async (row: AdminStudyGroup, archived: boolean) => {
    try {
      await archiveMutation.mutateAsync({ groupId: row.id, archived });
      toast.success(archived ? `${row.name} archived.` : `${row.name} restored.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not update the group.");
    }
  };

  return (
    <>
      <DataTable>
        <DataTableToolbar>
          <DataTableToolbarSpacer />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw
              aria-hidden="true"
              className={cn("size-4", query.isFetching && "animate-spin")}
            />
            Refresh
          </Button>
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} groups
          </span>
        </DataTableToolbar>
        <DataTableScroll>
          <Table>
            <caption className="sr-only">Study groups for moderation</caption>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Topic</TableHead>
                <TableHead className="text-right">Members</TableHead>
                <TableHead className="w-[1%] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            {query.isLoading ? (
              <DataTableSkeleton rows={8} columns={5} />
            ) : (
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">
                      <span className="inline-flex items-center gap-2">
                        {row.name}
                        {row.archived_at ? <StatusPill status="archived" /> : null}
                      </span>
                    </TableCell>
                    <TableCell>{row.subject ?? "—"}</TableCell>
                    <TableCell>{row.topic ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.member_count}/{row.max_members}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={
                            archiveMutation.isPending &&
                            archiveMutation.variables?.groupId === row.id
                          }
                          onClick={() => void setArchived(row, !row.archived_at)}
                        >
                          {row.archived_at ? "Restore" : "Archive"}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setActiveGroup(row)}>
                          Moderate
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>
        {!query.isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<Users aria-hidden="true" />}
            title="No study groups yet"
            description="Learners create groups in the portal — they appear here for moderation."
          />
        ) : null}
        {!query.isLoading && items.length > 0 ? (
          <DataTableFooter>
            <DataTablePagination
              page={page + 1}
              pageSize={pageSize}
              totalRows={total}
              onPageChange={(next) => setPage(Math.max(0, next - 1))}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(0);
              }}
              testIdPrefix="learning-study-groups"
            />
          </DataTableFooter>
        ) : null}
      </DataTable>

      <Sheet open={activeGroup != null} onOpenChange={(open) => !open && setActiveGroup(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>{activeGroup?.name ?? "Study group"}</SheetTitle>
            <SheetDescription>
              Review chat messages and take moderation action on reported content.
            </SheetDescription>
          </SheetHeader>
          {activeGroup ? (
            <>
              <BannedMembersPanel group={activeGroup} />
              <StudyGroupMessagesPanel group={activeGroup} />
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
